import {
  BadgeCheck, Bike, CalendarDays, Droplets, Dumbbell, Flame, Footprints, Hand, LifeBuoy, Lock, Mountain, PartyPopper,
  ShieldCheck, Sparkles, Tent, Thermometer, Trophy, Users, Waves,
} from 'lucide-react';
import SEO from '../components/SEO';
import { ServiceHero } from '../components/ServiceHeader';
import { ClubJourney } from '../components/ProgramsJourney';
import { TourClosing } from '../components/TourJourney';
import { MEDIA_SLOTS } from '../lib/mediaTruth';
import './AmenitiesPage.css';

// Amenities as a journey through the club's spaces (owner direction, Sept 30), the companion to the
// Programs journey. Every fact comes from the club's own pages; spaces still waiting for a photo
// show the club logo.
export const SPACES = [
  {
    slug: 'open-floor', name: 'Open Floor', label: 'Weight Rooms', rgb: '214 124 62', line: 'barbell', media: MEDIA_SLOTS.amenitiesStrength,
    title: ['Room to train,', 'your way.'],
    lede: 'Weight rooms with racks and free weights, with the Rockwall rising right beside them.',
    points: [
      [Dumbbell, 'Weight rooms with racks and free weights'],
      [Users, 'Coached weight training classes like Body Blast and F.I.T.'],
      [BadgeCheck, 'Weight training classes included with membership'],
    ],
    path: '/schedule/weight-training', cta: 'Explore Weight Training', next: ['#plan-your-visit', 'Plan a Tour'],
  },
  {
    slug: 'cardio-floor', name: 'Cardio Floor', rgb: '72 124 220', line: 'stride', media: MEDIA_SLOTS.amenitiesCardio,
    title: ['Keep', 'your pace.'],
    lede: 'A cardio floor lined with treadmills, plus group cardio classes from spin to kickboxing.',
    stat: ['50+', 'classes a week, included in membership'],
    points: [
      [Footprints, 'Treadmills on the cardio floor'],
      [Bike, 'Spin rides like The ZONE and Battle in the Saddle'],
      [Flame, 'Zumba, kickboxing, and Cardio at the Barre'],
    ],
    path: '/schedule/cardio', cta: 'Explore Cardio', next: ['#plan-your-visit', 'Plan a Tour'],
  },
  {
    slug: 'studios', name: 'Studios', label: 'Group Exercise', rgb: '158 128 220', line: 'cadence', media: MEDIA_SLOTS.cardioGroupClass,
    title: ['A studio', 'for every class.'],
    lede: 'Dedicated spaces for kickboxing, Reformer Pilates, cycling, MX4, and group exercise.',
    points: [
      [Flame, 'A kickboxing studio with heavy bags'],
      [Sparkles, 'Brentwood’s top Reformer Pilates studio'],
      [Bike, 'A cycling studio for spin classes'],
      [Dumbbell, 'The MX4 studio, also available to rent for team training'],
    ],
    path: '/programs', cta: 'Explore Programs', next: ['/schedule', 'See the Schedule'],
  },
  {
    slug: 'aquatics', name: 'Aquatics', label: 'Pools', rgb: '40 172 192', line: 'waves', media: MEDIA_SLOTS.amenitiesAquatics,
    title: ['Two indoor pools,', 'year-round.'],
    lede: 'A heated indoor lap pool and a separate heated lesson pool for swim lessons and therapy.',
    stat: ['93°F', 'warm-water teaching pool'],
    points: [
      [Waves, 'Lap lanes for members during club hours'],
      [Users, 'Water Aerobics and Advanced Swim Conditioning'],
      [LifeBuoy, 'Swim lessons in the warm-water teaching pool'],
    ],
    path: '/programs/swim', cta: 'Explore Swimming Lessons', next: ['#plan-your-visit', 'Plan a Tour'],
  },
  {
    slug: 'rockwall', name: 'Rockwall', rgb: '226 106 78', line: 'climb', media: MEDIA_SLOTS.amenitiesRockwall,
    title: ['Climb', 'two stories.'],
    lede: 'A two-story indoor climbing wall for members looking for an additional challenge.',
    stat: ['2-story', 'indoor climbing wall'],
    points: [
      [Mountain, 'Open to members once they earn the required certificate'],
      [Tent, 'Part of summer kids camps, with water games and crafts'],
      [PartyPopper, 'Available to rent for a memorable climbing experience'],
    ],
    path: '/rentals', cta: 'Rent the Rockwall', next: ['#plan-your-visit', 'Plan a Tour'],
  },
  {
    slug: 'courts', name: 'Courts', label: 'Basketball & Pickleball', rgb: '112 146 196', line: 'court', media: MEDIA_SLOTS.amenitiesBasketball,
    title: ['Game on,', 'indoors.'],
    lede: 'A full-sized indoor basketball court with six hoops and resurfaced flooring.',
    stat: ['6', 'hoops on a full-size court'],
    points: [
      [Trophy, 'Converts to indoor pickleball for a range of skill levels'],
      [CalendarDays, 'Cornhole and community events'],
      [Users, 'Available to rent for games and tournaments'],
    ],
    path: '/rentals', cta: 'Rent the Court', next: ['#plan-your-visit', 'Plan a Tour'],
  },
  {
    slug: 'locker-rooms', name: 'Locker Rooms', label: 'Sauna & Steam', rgb: '128 176 152', line: 'steam', media: MEDIA_SLOTS.amenitiesSauna, logoUntilPhoto: true,
    title: ['Recover', 'in comfort.'],
    lede: 'Gender-specific locker rooms with saunas and steam rooms, warm showers, and digital lockers.',
    points: [
      [Thermometer, 'Saunas and steam rooms in the locker rooms'],
      [Droplets, 'Private locker rooms with warm showers'],
      [Lock, 'Digital lockers for secure storage'],
      [Hand, 'HydroMassage and massage therapy for recovery'],
    ],
    path: '/programs/massage', cta: 'Explore Massage Therapy', next: ['#plan-your-visit', 'Plan a Tour'],
  },
  {
    slug: 'kids-club', name: 'Kids Club', rgb: '218 156 44', line: 'bounce', media: MEDIA_SLOTS.kidsFamilyHero,
    title: ['Endless fun', 'for little ones.'],
    lede: 'An impeccably clean Kids Center with a movie room, a mini basketball court, and air hockey.',
    stat: ['2-story', 'indoor jungle gym'],
    points: [
      [ShieldCheck, 'Background-checked, CPR-certified staff in a fully gated Kid’s Club'],
      [Sparkles, 'Cleaned around the clock by dedicated sanitization staff'],
      [CalendarDays, 'Open Monday–Thursday 8:00am–8:00pm, Friday–Sunday 8:00am–1:00pm'],
    ],
    path: '/programs/kids-and-family', cta: 'Explore Kids & Family', next: ['#plan-your-visit', 'Plan a Tour'],
  },
];

const INTRO = {
  eyebrow: 'Inside the club',
  title: ['Spaces for every', 'fitness level and interest.'],
  lede: 'Walk the club from the weight room to the Kids Club. Every stop shows the space, what it’s for, and how to see it in person.',
};

const AmenitiesPage = () => (
  <main className="dv-amenities" data-testid="amenities-page">
    <SEO
      title="Amenities | Delta Valley Health Club Brentwood"
      description="Explore the indoor pools, basketball and pickleball courts, Rockwall, sauna, steam rooms, locker rooms, and workout spaces at Delta Valley Health Club."
      keywords="gym amenities Brentwood CA, indoor pool, pickleball, rock climbing wall, sauna, steam room"
    />

    <ServiceHero
      id="amenities-title"
      media={MEDIA_SLOTS.amenitiesHero}
      haze="214 124 62"
      parent={{ label: 'Home', href: '/' }}
      current="Amenities"
      label="Amenities at Delta Valley"
      title={<><span>Amenities</span><em>at Delta Valley.</em></>}
      lede="Explore the spaces and features available at our Brentwood health club."
      actions={(
        <>
          <a className="dv-button dv-button--light" href="#amenities-spaces">Explore the Amenities</a>
          <a className="dv-button dv-button--outline-light" href="#plan-your-visit">Book a Tour</a>
        </>
      )}
      facts={['Two indoor pools', 'Two-story Rockwall', 'Sauna & steam in the locker rooms']}
    />

    <ClubJourney id="amenities-spaces" prefix="amenity" stops={SPACES} intro={INTRO} indexLabel="Jump to a space" railLabel="Club spaces" />

    <TourClosing />
  </main>
);

export default AmenitiesPage;
